<app-modal
  [isOpen]="imageService.isPopupOpen()"
  [title]="imageService.popupTitle()"
  [size]="isFullscreen() ? 'full' : '5xl'"
  [showCloseButton]="true"
  [closeOnBackdropClick]="true"
  [closeOnEscape]="true"
  (onClose)="onClose()"
>
  <!-- Toolbar -->
  <div class="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
    <div class="flex items-center gap-1">
      <!-- Zoom Out -->
      <button
        type="button"
        class="toolbar-btn"
        title="Zoom Out"
        (click)="zoomOut()"
        [disabled]="imageService.popupLoading() || !imageService.popupImageUrl()"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
        </svg>
      </button>

      <!-- Zoom Level Display -->
      <span class="text-sm text-gray-600 min-w-[60px] text-center">{{ zoomPercent() }}%</span>

      <!-- Zoom In -->
      <button
        type="button"
        class="toolbar-btn"
        title="Zoom In"
        (click)="zoomIn()"
        [disabled]="imageService.popupLoading() || !imageService.popupImageUrl()"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
        </svg>
      </button>

      <div class="w-px h-6 bg-gray-300 mx-2"></div>

      <!-- Rotate Left -->
      <button
        type="button"
        class="toolbar-btn"
        title="Rotate Left"
        (click)="rotateLeft()"
        [disabled]="imageService.popupLoading() || !imageService.popupImageUrl()"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
      </button>

      <!-- Rotate Right -->
      <button
        type="button"
        class="toolbar-btn"
        title="Rotate Right"
        (click)="rotateRight()"
        [disabled]="imageService.popupLoading() || !imageService.popupImageUrl()"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
        </svg>
      </button>

      <div class="w-px h-6 bg-gray-300 mx-2"></div>

      <!-- Reset -->
      <button
        type="button"
        class="toolbar-btn"
        title="Reset View"
        (click)="resetView()"
        [disabled]="imageService.popupLoading() || !imageService.popupImageUrl()"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
    </div>

    <div class="flex items-center gap-1">
      <!-- Fullscreen Toggle -->
      <button
        type="button"
        class="toolbar-btn"
        [title]="isFullscreen() ? 'Exit Fullscreen' : 'Fullscreen'"
        (click)="toggleFullscreen()"
        [disabled]="imageService.popupLoading() || !imageService.popupImageUrl()"
      >
        @if (isFullscreen()) {
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
          </svg>
        } @else {
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
          </svg>
        }
      </button>

      <div class="w-px h-6 bg-gray-300 mx-2"></div>

      <!-- Download -->
      <button
        type="button"
        class="toolbar-btn"
        title="Download Image"
        (click)="downloadImage()"
        [disabled]="imageService.popupLoading() || !imageService.popupImageUrl()"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      </button>

      <!-- Open in New Tab -->
      <button
        type="button"
        class="toolbar-btn"
        title="Open in New Tab"
        (click)="openInNewTab()"
        [disabled]="imageService.popupLoading() || !imageService.popupImageUrl()"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </button>
    </div>
  </div>

  <!-- Image Container -->
  <div 
    class="image-container"
    [class.cursor-grab]="!isDragging()"
    [class.cursor-grabbing]="isDragging()"
    (mousedown)="onMouseDown($event)"
    (mousemove)="onMouseMove($event)"
    (mouseup)="onMouseUp()"
    (mouseleave)="onMouseUp()"
    (wheel)="onWheel($event)"
  >
    @if (imageService.popupLoading()) {
      <div class="loading-container">
        <div class="spinner"></div>
        <span class="text-gray-500 mt-3">Loading image...</span>
      </div>
    } @else if (imageService.popupImageUrl()) {
      <div 
        class="image-wrapper"
        [style.transform]="imageTransform()"
      >
        <img
          #imageElement
          [src]="imageService.popupImageUrl()"
          [alt]="imageService.popupTitle()"
          class="displayed-image"
          draggable="false"
          (load)="onImageLoad()"
          (error)="onImageError($event)"
        />
      </div>
    } @else {
      <div class="empty-container">
        <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span class="text-gray-500 mt-3">No image available</span>
      </div>
    }
  </div>

  <!-- Footer Info -->
  @if (imageService.popupImageUrl() && !imageService.popupLoading()) {
    <div class="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex justify-between">
      <span>Scroll to zoom • Drag to pan</span>
      <span>{{ rotation() }}° rotation</span>
    </div>
  }
</app-modal>
